import { Box, Group, Skeleton, Text, UnstyledButton } from "@mantine/core"
import { useTranslation } from "react-i18next"
import classes from "./catalog.module.css"
import { describePromotion, formatDate, promoIcon, type PromoGroup } from "./price-labels"

type Props = {
  groups: PromoGroup[]
  /** Promotions the price service says reach this buyer; while prices load, hold their space. */
  expected?: number
  activeId: string | null
  onSelect: (promotionId: string | null) => void
  onViewAll: () => void
}

const VISIBLE = 4

/**
 * «Танд хамаарах урамшуулал» (canvas OrdB_Catalog): up to four promotion cards
 * that actually touch this buyer's catalogue. Clicking one filters the grid to
 * its products. Renders nothing when there is no promotion, instead of the old
 * full-width banner.
 */
export function PromoStrip({ groups, expected = 0, activeId, onSelect, onViewAll }: Props) {
  const { t } = useTranslation("order")
  if (groups.length === 0) return expected > 0 ? <PromoStripSkeleton count={expected} /> : null

  return (
    <Box component="section" aria-label={t("catalog.promoStripTitle")}>
      <Group justify="space-between" mb="xs">
        <Text fz="var(--nw-fz-card)" fw={650}>
          {t("catalog.promoStripTitle")}
          <Text span inherit fw={400} c="var(--nw-text-2)">
            {` · ${groups.length}`}
          </Text>
        </Text>
        <UnstyledButton className={classes.linkButton} onClick={onViewAll}>
          {t("catalog.viewAll")}
        </UnstyledButton>
      </Group>
      <Box className={classes.promoGrid}>
        {groups.slice(0, VISIBLE).map((g) => (
          <UnstyledButton
            key={g.promotionId}
            className={classes.promoCard}
            data-active={activeId === g.promotionId || undefined}
            aria-pressed={activeId === g.promotionId}
            onClick={() => onSelect(activeId === g.promotionId ? null : g.promotionId)}
          >
            <Group justify="space-between" wrap="nowrap" gap="xs">
              <Box component="span" className={classes.pill}>
                {promoIcon(g.kind)}
                {t(`catalog.promoType.${g.kind}`)}
              </Box>
              {g.effectiveTo && (
                <Text span className={classes.meta}>
                  {t("catalog.untilDate", { date: formatDate(g.effectiveTo) })}
                </Text>
              )}
            </Group>
            <Text fz="var(--nw-fz-lead)" fw={650} lineClamp={1}>
              {g.name || "-"}
            </Text>
            <Text className={classes.meta} lineClamp={1}>
              {describePromotion(t, g.rule)}
            </Text>
            <Text className={classes.meta}>{t("catalog.productsCount", { count: g.products.length })}</Text>
          </UnstyledButton>
        ))}
      </Box>
    </Box>
  )
}

/**
 * Same footprint as the strip (title row + promo cards), so the catalogue below does not jump
 * when the promotions arrive with the prices.
 */
export function PromoStripSkeleton({ count }: Readonly<{ count: number }>) {
  return (
    <Box aria-hidden>
      <Group justify="space-between" mb="xs">
        <Skeleton h={22} w={220} radius="sm" />
        <Skeleton h={18} w={90} radius="sm" />
      </Group>
      <Box className={classes.promoGrid}>
        {Array.from({ length: Math.min(count, VISIBLE) }).map((_, i) => (
          <Box key={i} className={classes.promoCard}>
            <Group justify="space-between" wrap="nowrap" gap="xs">
              <Skeleton h={24} w={72} radius="xl" />
              <Skeleton h={16} w={110} radius="sm" />
            </Group>
            <Skeleton h={22} w="70%" radius="sm" />
            <Skeleton h={18} w="90%" radius="sm" />
            <Skeleton h={18} w="40%" radius="sm" />
          </Box>
        ))}
      </Box>
    </Box>
  )
}
